import { useMemo, useState } from 'react'
import { DatePicker, Modal, Select, Space, Tag, Tooltip, Typography } from 'antd'
import type { TagProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { type Dayjs } from 'dayjs'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { defaultDayRange, rangeToIso } from '@/shared/lib/dayRange'
import { DataTable } from '@/shared/ui/table/DataTable'
import { PageHeader } from '@/shared/ui/PageHeader'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { shortId } from '@/shared/lib/shortId'
import { AuthedImage } from '@/features/attendance/AuthedImage'
import { DeviceDirectionTag } from '@/features/attendance/DeviceDirectionTag'
import { verifyMethodLabel } from '@/features/attendance/verifyMode'
import {
  eventPictureUrl,
  fetchDeviceOptions,
  fetchEmployeeOptions,
  fetchEventPage,
  type AttendanceEvent,
  type EventResult,
} from '@/features/attendance/attendanceApi'

const { RangePicker } = DatePicker

/**
 * Standart oraliq — OXIRGI 7 KUN (bugun ham ichida).
 *
 * <h2>⚠️ ILGARI BU 1 KUN EDI VA EKRAN JIMGINA BO'SH TURARDI</h2>
 * Tashqi tester «EVENTS EMPTY» deb yozgan (BUG-052) va 2026-08-16 da
 * o'lchov uni TASDIQLADI — lekin sabab «ma'lumot yo'q» emas edi:
 *
 * <pre>
 * attendance.event                             → 258 qator, hammasi af-isp
 * tenant zonasi (platform.tenant.day_timezone) → Asia/Kabul (UTC+04:30)
 *
 * tenant kalendari bo'yicha kunlar:
 *   2026-08-15 → 40    2026-08-14 → 47    2026-08-13 → 53
 *   2026-08-12 → 49    2026-08-11 → 69
 *
 * panel AYNAN shuni so'rardi (bir kunlik oyna):
 *   ?from=2026-08-15T19:30:00Z&to=2026-08-16T19:29:59.999Z → 0 qator
 * </pre>
 *
 * <p>⚠️ ILDIZ — KUN CHEGARASI. Kobulda sutka `19:30Z` da boshlanadi,
 * terminal esa ish vaqtida, ya'ni `08:00Z`–`18:00Z` oralig'ida yozadi.
 * Demak HAR KUNI `19:30Z` dan ertangi `08:00Z` gacha — kamida O'N
 * IKKI SOAT — bir kunlik oyna PRINSIPIAL ravishda bo'sh bo'ladi,
 * jurnalda 258 yozuv turgan holda. Terminal bir kun dam olsa, oyna
 * butun sutka bo'sh.
 *
 * <p>⚠️ «Hozir kim kirdi» degan savol YO'QOLMADI — aksincha, endi
 * javob BOR. Ro'yxat `event_time` bo'yicha kamayish tartibida keladi,
 * ya'ni eng yangi o'tish baribir eng tepada turadi. Bo'sh quti esa
 * hech qanday savolga javob bermasdi.
 *
 * <p>⚠️ Hajm e'tirozi kuchini yo'qotmadi, shuning uchun oyna
 * OLIB TASHLANMADI (`allowClear={false}` quyida saqlangan) — u
 * `AuditPage` dagi bilan tenglashtirildi, xolos. Ro'yxat sahifalanadi
 * (`size=25`), ya'ni kengroq oyna qatorlarni emas, faqat SO'ROV
 * doirasini kengaytiradi.
 */
const defaultRange = (): [Dayjs, Dayjs] => defaultDayRange(6)

/**
 * RANG — HARAKATGA qarab.
 *
 *   · `GRANTED` YASHIL  — tanildi va o'tkazildi
 *   · `DENIED`  QIZIL   — tanilmadi/rad etildi. ⚠️ Bu ham davomat fakti:
 *                         kimdir eshik oldida TURGAN. Uni ko'rsatmaslik
 *                         "hech kim kelmagan" degan yolg'on xulosaga olib
 *                         kelardi, holbuki odam kelgan va o'ta olmagan —
 *                         bu tergov kerak bo'lganda birinchi qidiriladigan
 *                         yozuv
 *   · `DOOR`    KULRANG — eshikning o'z hodisasi, xodimsiz. Diqqat talab
 *                         qilmaydi va ro'yxatni shovqinlashtirmasligi kerak
 *   · `OTHER`   SARIQ   — tasniflanmagan kod. ⚠️ Backend noma'lum `minor`
 *                         ni ATAYLAB shu yerga qo'yadi, `GRANTED` ga emas
 *                         (`DeviceEventRecord.classify`). Sariq — chunki
 *                         bu "ma'lumot yo'q" emas, "ma'lumot BOR, lekin
 *                         biz uni hali nomlamaganmiz" degani
 */
const RESULT_COLOR: Record<EventResult, TagProps['color']> = {
  GRANTED: 'green',
  DENIED: 'red',
  DOOR: 'default',
  OTHER: 'gold',
}

/** Filtrda faqat ikkitasi — quyidagi izohga qarang */
const FILTERABLE_RESULTS: readonly EventResult[] = ['GRANTED', 'DENIED']

/**
 * Qurilmadan kelgan hodisalar jurnali.
 *
 * <h2>⚠️ VAQT USTUNI IKKI QIYMATNI KO'RSATADI</h2>
 * Bu sahifaning eng qimmatli xususiyati. Katakda tenant zonasiga
 * o'girilgan vaqt turadi, tooltip'da esa QURILMA YOZGAN xom qiymat
 * (`2026-08-11T16:39:32+08:00`).
 *
 * Nega bu shunchalik muhim: 2026-08-11 da haqiqiy qurilma ekranda Toshkent
 * vaqtini ko'rsatib turib, ISAPI javobida uni `+08:00` (Shanghai) deb
 * belgilagan. Har hodisa bazaga 3 SOATGA SURILGAN holda tushardi va buni
 * hech kim sezmasdi — qurilma oldida turgan odam ekranda to'g'ri vaqtni
 * ko'radi, hisobot esa jimgina noto'g'ri chiqadi. Ikkala qiymatni YONMA-YON
 * ko'rsatish — bu nosozlikni umuman ko'rish mumkin bo'lgan YAGONA joy.
 *
 * <h2>⚠️ USUL USTUNI</h2>
 * "Qanaqa usulda o'tdi" — bu sahifaga aynan shu savol bilan kelinadi.
 * Xom `minor` (75, 38, 76) hech narsa demaydi. Tarjima `verifyMode.ts` da
 * va u FAQAT haqiqiy qurilmada o'lchangan kodlarni biladi; qolganlari xom
 * kodi bilan "Noma'lum (kod N)" bo'lib chiqadi — yashirilmaydi.
 *
 * <h2>⚠️ NATIJA FILTRIDA IKKITA QIYMAT, TO'RTTA EMAS</h2>
 * `GRANTED` va `DENIED` — bular ODAM haqidagi savollar va operator aynan
 * shularni ajratadi. `DOOR` va `OTHER` filtrda YO'Q, lekin ustunda
 * KO'RINADI: birinchisi (eshik ochildi/yopildi) alohida filtr sifatida
 * deyarli hech qachon kerak bo'lmaydi, ikkinchisi esa vaqtinchalik
 * toifa — u bo'shashi kerak, uni filtrga chiqarish esa mavjudligini
 * qonuniylashtirardi.
 */
export function AttendanceEventsPage() {
  const { t } = useTranslation()

  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange)
  const [deviceId, setDeviceId] = useState<string | null>(null)
  const [employeeNo, setEmployeeNo] = useState<string | null>(null)
  const [result, setResult] = useState<EventResult | null>(null)
  const [picture, setPicture] = useState<AttendanceEvent | null>(null)

  // ⚠️ TENANT zonasida: `dayjs()` brauzernikini olardi (`dayRange.ts`)
  const { from, to } = rangeToIso(range)

  const devices = useQuery({
    queryKey: queryKeys.attendance.deviceOptions,
    queryFn: () => fetchDeviceOptions(),
    staleTime: 5 * 60_000,
  })
  const employees = useQuery({
    queryKey: queryKeys.attendance.employeeOptions,
    queryFn: () => fetchEmployeeOptions(),
    staleTime: 5 * 60_000,
  })

  const employeeOptions = useMemo(
    () => (employees.data ?? []).map((e) => ({ value: e.employeeNo, label: e.label })),
    [employees.data],
  )

  const filter = useMemo(
    () => ({ from, to, deviceId, employeeNo, result }),
    [from, to, deviceId, employeeNo, result],
  )
  // Filtr o'zgarganda YANGI so'rov: kesh kaliti ham shu obyektdan quriladi
  // (`queryKeys.attendance.events`), aks holda eski davr ekranda qolardi.
  const fetchPage = useMemo(() => fetchEventPage(filter), [filter])

  const columns = useMemo<ColumnsType<AttendanceEvent>>(
    () => [
      {
        key: 'time',
        title: t('attendance.event.time'),
        width: 190,
        render: (_, row) => <EventTimeCell event={row} />,
      },
      {
        key: 'employee',
        title: t('attendance.event.employee'),
        width: 220,
        render: (_, row) => <EmployeeCell event={row} />,
      },
      {
        key: 'device',
        title: t('attendance.event.device'),
        width: 170,
        render: (_, row) => (
          <Typography.Text>
            {row.deviceName ?? (
              // ⚠️ B5: bu shox 2026-08-14 gacha HAR QATORDA ishlardi —
              //    server `deviceName` ni umuman yubormasdi va tester
              //    ekranda `3ae69369` ni ko'rgan. Endi u O'LIK bo'lishi
              //    kerak (`EventDeviceNameTest`), lekin OLIB
              //    TASHLANMAYDI: `event_device_fk` dagi kaskad bir kun
              //    olib tashlansa jurnal jimgina bo'shab qolmasin.
              //
              // ⚠️ Qisqartirish SHU YERDA yozilmaydi — `shortId` da,
              //    B4 dagi aktyor bilan BIR XIL funksiya. Ilgari bu
              //    yerda uchtasiz `slice(0, 8)` turardi va o'sha
              //    identifikator boshqa ekranda `…` bilan chizilardi.
              <Typography.Text code style={{ fontSize: 12 }}>
                {shortId(row.deviceId)}
              </Typography.Text>
            )}
          </Typography.Text>
        ),
      },
      {
        /*
          ⚠️ YO'NALISH HODISADAN, QURILMANING BUGUNGI SOZLAMASIDAN
          EMAS. Server uni yozuv paytida muhrlaydi
          (`attendance.event.direction`), ya'ni terminal keyin boshqa
          eshikka ko'chirilsa bu ustun O'ZGARMAYDI — jurnal "o'sha
          payt nima ma'lum edi" ni ko'rsatadi.

          ⚠️ Tooltipsiz (`compact`): jurnalda qatorlar ko'p va har
          birida izoh ochilishi o'qishni qiyinlashtirardi. Tushuntirish
          qurilmalar ro'yxatida, ya'ni qiymat QO'YILADIGAN joyda.
        */
        key: 'direction',
        title: t('attendance.direction'),
        width: 120,
        render: (_, row) => <DeviceDirectionTag direction={row.direction} compact />,
      },
      {
        // ⚠️ MAJBURIY USTUN — komponent sarlavhasidagi izoh.
        key: 'method',
        title: t('attendance.event.methodColumn'),
        width: 150,
        render: (_, row) => <MethodCell event={row} />,
      },
      {
        key: 'result',
        title: t('attendance.event.result'),
        width: 130,
        render: (_, row) => (
          <Tooltip title={t(`attendance.event.resultHint.${row.result}`)}>
            <Tag color={RESULT_COLOR[row.result]}>{t(`attendance.event.results.${row.result}`)}</Tag>
          </Tooltip>
        ),
      },
      {
        key: 'picture',
        title: t('attendance.event.picture'),
        width: 90,
        render: (_, row) => (
          <AuthedImage
            url={eventPictureUrl(row.id)}
            present={row.hasPicture}
            width={40}
            height={40}
            alt={row.displayName ?? row.employeeNo ?? ''}
            emptyHint={t('attendance.event.noPictureHint')}
            onClick={() => setPicture(row)}
          />
        ),
      },
    ],
    [t],
  )

  return (
    <>
      <PageHeader title={t('nav.attendanceEvents')} description={t('pageIntro.attendanceEvents')} />

      <Space wrap style={{ marginBottom: 16 }}>
        {/* ⚠️ `allowClear={false}` — sana oralig'isiz so'rov butun tarixni
            tortishga urinardi (`attendance.event` cheksiz o'sadi). Bo'sh
            holatga tushirishning iloji YO'Q. */}
        <RangePicker
          value={range}
          onChange={(value) => {
            if (value?.[0] && value[1]) setRange([value[0], value[1]])
          }}
          allowClear={false}
          format="DD.MM.YYYY"
        />

        <Select<string | null>
          value={deviceId}
          onChange={setDeviceId}
          allowClear
          showSearch
          optionFilterProp="label"
          loading={devices.isPending}
          placeholder={t('attendance.event.deviceFilter')}
          style={{ minWidth: 220 }}
          options={[...(devices.data ?? [])]}
        />

        {/* ⚠️ Qiymat `employeeNo`, `id` EMAS — va `fetchEmployeeOptions`
            ikkalasini ham beradi, aynan shu farq uchun. Hodisada xodim
            MATN bilan bog'langan (`event.employee_no`, FK'siz), ya'ni id
            bo'yicha filtrlash reyestrda yo'q odamning yozuvlarini —
            tekshiruvda birinchi qidiriladiganlarini — ko'rinmas qilardi.
            Oylik hisobotda esa TESKARISI: u `employeeId` ni oladi. */}
        <Select<string | null>
          value={employeeNo}
          onChange={setEmployeeNo}
          allowClear
          showSearch
          optionFilterProp="label"
          loading={employees.isPending}
          placeholder={t('attendance.event.employeeFilter')}
          style={{ minWidth: 240 }}
          options={employeeOptions}
        />

        <Select<EventResult | null>
          value={result}
          onChange={setResult}
          allowClear
          placeholder={t('attendance.event.resultFilter')}
          style={{ minWidth: 170 }}
          options={FILTERABLE_RESULTS.map((value) => ({
            value,
            label: t(`attendance.event.results.${value}`),
          }))}
        />
      </Space>

      <DataTable<AttendanceEvent>
        queryKey={queryKeys.attendance.events(filter)}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="attendance-events"
        scrollX={1170}
        emptyTitle={t('attendance.event.empty')}
        emptyDescription={t('attendance.event.emptyHint')}
      />

      <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
        {t('attendance.event.timeSourceHint')}
      </Typography.Paragraph>

      {/* ⚠️ Kadr KATTALASHTIRILADI. 40×40 da yuz tanilmaydi — jadvaldagi
          rasm faqat "kadr bor" degan belgi. Tergov kerak bo'lganda esa
          aynan yuzni ko'rish kerak, va bu sahifadan chiqmasdan bo'lishi
          shart: yangi oynada ochish tokenli so'rovni talab qilardi. */}
      <Modal
        open={picture !== null}
        title={t('attendance.event.pictureTitle')}
        onCancel={() => setPicture(null)}
        footer={null}
        width={520}
        destroyOnHidden
      >
        {picture ? (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <AuthedImage
              url={eventPictureUrl(picture.id)}
              present={picture.hasPicture}
              width={472}
              height={354}
              alt={picture.displayName ?? picture.employeeNo ?? ''}
            />
            <Typography.Text type="secondary">
              {formatDateTime(picture.eventTime)} ·{' '}
              {picture.displayName ?? picture.employeeNo ?? t('attendance.event.unknownPerson')}
            </Typography.Text>
          </Space>
        ) : null}
      </Modal>
    </>
  )
}

/**
 * Vaqt + QURILMA XOM VAQTI.
 *
 * ⚠️ Xom qiymat tooltip'ga "qo'shimcha ma'lumot" sifatida qo'yilmagan —
 * u BU YERDA BO'LISHINING SABABI. Qurilma zonasi noto'g'ri bo'lsa
 * (o'lchangan holat) farqni faqat shu ikkovini yonma-yon qo'yib ko'rish
 * mumkin. Kodda "to'g'rilash" qilinmaydi: yashirin yamoq qurilma
 * tuzatilgan kuni teskari buzilardi.
 */
function EventTimeCell({ event }: { event: AttendanceEvent }) {
  const { t } = useTranslation()

  const lines = [utcTooltip(event.eventTime)]
  if (event.deviceTimeRaw) {
    lines.push(`${t('attendance.event.deviceRawTime')}: ${event.deviceTimeRaw}`)
  }

  return (
    <Tooltip
      title={lines
        .join('\n')
        .split('\n')
        .map((line, index) => (
          <div key={index}>{line}</div>
        ))}
    >
      <Typography.Text>{formatDateTime(event.eventTime)}</Typography.Text>
    </Tooltip>
  )
}

/**
 * Xodim: ism + tabel raqami.
 *
 * ⚠️ UCH HOLAT, IKKITA EMAS:
 *   · reyestrda bor          — ism va raqam
 *   · qurilma ism aytdi, biz bilmaymiz — ism ko'rsatiladi, lekin "reyestrda
 *     yo'q" belgisi bilan. Bu yozuv o'chirilgan yoki boshqa joydan
 *     qo'shilgan xodim degani va aynan shu holat tergovda qidiriladi
 *   · umuman noma'lum        — `minor=76` (yuz tanilmadi). Bu ODATIY hol,
 *     nosozlik emas: begona odam terminalga qaragan
 */
function EmployeeCell({ event }: { event: AttendanceEvent }) {
  const { t } = useTranslation()

  if (event.employeeNo === null && event.displayName === null) {
    return (
      <Tooltip title={t('attendance.event.unknownPersonHint')}>
        <Typography.Text type="secondary" italic>
          {t('attendance.event.unknownPerson')}
        </Typography.Text>
      </Tooltip>
    )
  }

  return (
    <Space direction="vertical" size={0}>
      <Space size={6}>
        <Typography.Text>{event.displayName ?? t('attendance.event.noName')}</Typography.Text>
        {event.employeeNo !== null && event.employeeId === null ? (
          <Tooltip title={t('attendance.event.notInRegistryHint')}>
            <Tag color="gold">{t('attendance.event.notInRegistry')}</Tag>
          </Tooltip>
        ) : null}
      </Space>
      {event.employeeNo === null ? null : (
        <Typography.Text code style={{ fontSize: 12 }}>
          {event.employeeNo}
        </Typography.Text>
      )}
    </Space>
  )
}

/**
 * Usul — `minor` dan, `verifyMode` esa tooltip'da.
 *
 * ⚠️ `verifyMode` ASOSIY MANBA EMAS: u har yozuvda bo'lmaydi (o'lchangan
 * javobda `minor=22` da umuman yo'q edi), `minor` esa har doim bor.
 * Lekin u YO'QOLMAYDI ham — qurilmaning o'z qiymati diagnostikada
 * kerak bo'lishi mumkin.
 */
function MethodCell({ event }: { event: AttendanceEvent }) {
  const { t } = useTranslation()

  const lines: string[] = []
  if (event.minor !== null) lines.push(`minor: ${event.minor}`)
  if (event.verifyMode) lines.push(`verifyMode: ${event.verifyMode}`)

  const label = <Typography.Text>{verifyMethodLabel(event.minor, t)}</Typography.Text>
  if (lines.length === 0) return label

  return (
    <Tooltip
      title={lines.map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    >
      {label}
    </Tooltip>
  )
}
